<template>
  <div class="container pap-form">
    <AlertCard
      v-if="loadingError.isError"
      type="error"
      header="There was a problem..."
    >
      {{ loadingError.message }}
      <p v-if="documentId">Document ID: {{ documentId }}</p>
    </AlertCard>

    <AlertCard
      v-if="showEditResult && resultType === 'error'"
      :type="resultType"
      :header="resultHeader"
      :message="resultMessage"
    >
      <p v-if="documentId">Document ID: {{ documentId }}</p>
    </AlertCard>

    <div
      v-if="showEditResult && resultType === 'success'"
      class="wrapper--center fw-300"
    >
      <CardImage
        :image="uploadedImage"
        header-type="alert"
        alert-header="Text Edit Successful!"
        type="pending"
        :showHeader="true"
        :showButtonPanel="false"
        :showProductsPanel="false"
        :showUploadDate="false"
      />
    </div>

    <div v-if="showForm" class="card">
      <FormTitle title="Edit Text" iconclass="far fa-edit mr-2" />

      <div class="card-body">
        <router-link to="/images-list-pending">
          <div class="pap-form__back">
            <i class="fas fa-caret-left"></i> Back
          </div>
        </router-link>
        <div
          class="pap-form__section pap-form__section--light-gray pap-form__section--mb-2"
        >
          <div class="pap-form__image-wrapper">
            <img alt="" :src="imageThumbURL" />
          </div>
          <p class="mb-0 mt-2 text-center font-weight-bold">{{ caption }}</p>
        </div>

        <b-form novalidate @submit.prevent="doEdit">
          <!-- -------------------------------- Dog name  -->
          <b-form-group
            id="dogName-group"
            label="Dog Name"
            label-for="dogName"
            label-class="label-bold"
            :invalid-feedback="form.dogName.errorMsg"
            :state="form.dogName.valid"
          >
            <p class="form-text text-secondary text-smaller-1 px-3 pb-1 mb-1">
              List names of <strong>all</strong> dogs in the order in which they
              appear in the image.
            </p>
            <b-form-input
              id="dogName"
              :maxlength="fieldMaxLength('dogName')"
              placeholder="Dog Name"
              @input="fieldOnInput('dogName')"
              v-model="form.dogName.value"
              :state="form.dogName.valid"
              trim
            ></b-form-input>
          </b-form-group>

          <!-- -------------------------------- Breed  -->
          <SelectBreed
            id="breed-id"
            label="Breed"
            :selected="form.breedId.value"
            :state="form.breedId.valid"
            :errorMsg="form.breedId.errorMsg"
            @breed-selected="setBreed($event)"
          />

          <!-- -------------------------------- Breed Desc   **CONDITIONAL if breedId is 'mixed' -->
          <b-form-group
            v-show="form.breedId.value === 'mixed'"
            id="breed-desc-group"
            label="Breed Description"
            label-for="breedDesc"
            label-class="label-bold"
            :invalid-feedback="form.breedDesc.errorMsg"
            :state="form.breedDesc.valid"
          >
            <p class="form-text text-secondary text-smaller-1 px-3 pb-1 m-0">
              For <strong>Breed</strong> you selected 'Mixed' - Briefly describe
              your mixed breed.
            </p>
            <p class="form-text text-secondary text-smaller-1 px-3 pb-1 m-0">
              If more than one breed is represented in the photo (example: two
              dogs in pic, each is its own breed), list the breed of each dog
              here. You <strong>must</strong> use this field; if you need more
              space, continue your entry in the Note field below.
            </p>
            <b-form-input
              id="breed-desc"
              :maxlength="fieldMaxLength('breedDesc')"
              placeholder="Describe your mixed breed"
              @input="fieldOnInput('breedDesc')"
              v-model="form.breedDesc.value"
              :state="form.breedDesc.valid"
              trim
            ></b-form-input>
          </b-form-group>

          <!-- -------------------------------- Country  -->
          <SelectCountry
            id="country"
            label="Country / Territory"
            :selected="form.country.value"
            :state="form.country.valid"
            :errorMsg="form.country.errorMsg"
            @country-selected="setField($event, 'country')"
          />

          <!-- -------------------------------- State **CONDITIONAL if country is US -->
          <SelectState
            v-if="form.country.value === 'US'"
            id="state"
            label="State"
            :selected="form.state.value"
            :state="form.state.valid"
            :errorMsg="form.state.errorMsg"
            @state-selected="setField($event, 'state')"
          />

          <!-- ------------------------ Province  **CONDITIONAL if country is *not* US -->
          <ProvinceField
            v-show="
              form.country.value !== 'US' &&
              form.country.value !== '' &&
              form.country.value !== '-'
            "
            id="province"
            :maxlength="fieldMaxLength('province')"
            label="Province / Region"
            placeholder="Province / Region"
            :key="componentKey"
            :init="form.province.value"
            :state="form.province.valid"
            :errorMsg="form.province.errorMsg"
            @province-value-input="setField($event, 'province')"
          />

          <!-- -------------------------------- City -->
          <b-form-group
            id="city-group"
            label="City"
            label-for="city"
            label-class="label-bold"
            :invalid-feedback="form.city.errorMsg"
            :state="form.city.valid"
          >
            <b-form-input
              id="city"
              :maxlength="fieldMaxLength('city')"
              placeholder="City"
              @input="fieldOnInput('city')"
              v-model="form.city.value"
              :state="form.city.valid"
              trim
            ></b-form-input>
          </b-form-group>

          <!-- -------------------------------- First name  -->
          <b-form-group
            id="fName-group"
            label="Your First Name"
            label-for="fName"
            label-class="label-bold"
            :invalid-feedback="form.fName.errorMsg"
            :state="form.fName.valid"
          >
            <b-form-input
              id="fName"
              :maxlength="fieldMaxLength('fName')"
              placeholder="First Name"
              @input="fieldOnInput('fName')"
              v-model="form.fName.value"
              :state="form.fName.valid"
              trim
            ></b-form-input>
          </b-form-group>

          <!-- -------------------------------- Last name  -->
          <b-form-group
            id="lName-group"
            label="Your Last Name"
            label-for="lName"
            label-class="label-bold"
            :invalid-feedback="form.lName.errorMsg"
            :state="form.lName.valid"
          >
            <b-form-input
              id="lName"
              :maxlength="fieldMaxLength('lName')"
              placeholder="Last Name"
              @input="fieldOnInput('lName')"
              v-model="form.lName.value"
              :state="form.lName.valid"
              trim
            ></b-form-input>
          </b-form-group>

          <!-- -------------------------------- Caption Info  -->
          <CaptionSection
            id="caption"
            label="Image Caption"
            :form="form"
            @caption-last-name-toggle="caption = $event"
          />

          <!-- -------------------------------- Special Project Code  -->
          <b-form-group
            id="specProjCode-group"
            label-for="specProjCode"
            label-class="label-bold"
            :invalid-feedback="form.specProjCode.errorMsg"
            :state="form.specProjCode.valid"
          >
            <slot name="label">
              <ToggleControl toggleId="specialProjectCode">
                <span class="label-bold">Special Project Code (optional)</span>
              </ToggleControl>
            </slot>
            <b-collapse
              id="specialProjectCode"
              :visible="form.specProjCode.value !== ''"
            >
              <div class="mx-3 my-2">
                <ToggleControl
                  toggle-id="specialProjectCodeText"
                  closed-text="Learn more..."
                  open-text="- hide -"
                  toggle-class="text-secondary mb-2"
                >
                  <template v-slot:default="slotProps">
                    {{ slotProps.togglerText.display }}
                  </template>
                </ToggleControl>
                <b-collapse id="specialProjectCodeText">
                  <SpecialProjectCodeText />
                </b-collapse>
              </div>
              <b-form-input
                id="specProjCode"
                :maxlength="fieldMaxLength('specProjCode')"
                placeholder="Special Project Code"
                @input="fieldOnInput('specProjCode')"
                v-model="form.specProjCode.value"
                :state="form.specProjCode.valid"
                trim
              ></b-form-input>
            </b-collapse>
          </b-form-group>

          <!-- -------------------------------- Note  -->
          <b-form-group
            id="note-group"
            label-for="note"
            label-class="label-bold"
            :invalid-feedback="form.note.errorMsg"
            :state="form.note.valid"
          >
            <slot name="label">
              <ToggleControl toggleId="optionalNote">
                <span class="label-bold">Note (optional)</span>
              </ToggleControl>
            </slot>
            <b-collapse id="optionalNote" :visible="form.note.value !== ''">
              <p class="form-text text-secondary text-smaller-1 px-3 pb-1 m-0">
                If you need or want to supply additional information about the
                image you're uploading, enter it here.
              </p>
              <p class="text-primary text-smaller-1 m-2">
                # Characters: {{ noteLength }} /
                {{ fieldMaxLength('note') }}
              </p>
              <b-form-textarea
                id="note"
                placeholder="Note..."
                rows="5"
                :maxlength="fieldMaxLength('note')"
                @input="fieldOnInput('note')"
                v-model="form.note.value"
                :state="form.note.valid"
              ></b-form-textarea>
            </b-collapse>
          </b-form-group>

          <FormErrorAlert
            :showAlert="showFormActionError"
            :msg="formErrorMsg"
          />

          <button
            @click="setSubmitted"
            type="submit"
            class="btn btn-block btn-submit pap-form__btn"
          >
            Submit
          </button>
        </b-form>
      </div>
    </div>
  </div>
</template>

<script>
import SelectBreed from '@/components/form/FormSelectListBreeds';
import SelectCountry from '@/components/form/FormSelectListCountries';
import SelectState from '@/components/form/FormSelectListStates';
import ProvinceField from '@/components/form/FormProvinceField';
import CaptionSection from '@/components/form/FormCaptionSection';
import SpecialProjectCodeText from '@/components/text/SpecialProjectCode';

import AlertCard from '@/components/notifications/AlertCard';
import CardImage from '@/components/cards/CardImage';

import FormTitle from '@/components/form/FormTitle.vue';
import FormErrorAlert from '@/components/form/FormErrorAlert.vue';

import ToggleControl from '@/components/layout/ToggleControl';

import { Upload } from '@/models/Upload';

export default {
  name: 'PageImageTextEdit',
  components: {
    SelectBreed,
    SelectCountry,
    SelectState,
    ProvinceField,
    CaptionSection,
    SpecialProjectCodeText,
    AlertCard,
    CardImage,
    FormTitle,
    FormErrorAlert,
    ToggleControl,
  },
  data: function () {
    return {
      documentId: '',

      caption: '',
      imageThumbURL: '',

      // Error reporting: Loading error: Problem loading page, i.e. problem fetching imageUpload document for display
      loadingError: {
        isError: false,
        message: '',
      },
      loadingSuccess: false, // Avoids showing the form while document is being fetched from db

      showEditResult: false,
      resultType: '',
      resultSuccessMsg: 'Image upload text successfully edited.',
      resultErrorMsg: '',

      // The upload data model, i.e. this is what's populated via user form data and other programmatically generated data; this is
      // source of data for document in imageUploads collection.
      upload: null,

      imageFilename: '',

      // Set after successful edit; used as prop (named 'image') in CardImage component which is being used as a success alert.
      // THAT comp expects and 'image' prop, which is an obj.
      uploadedImage: {
        docStatus: 'ok',
        creTime: Date.now(),
        dogName: '',
        breed: '',
        caption: '',
        imageThumbURL: '',
      },

      // Form data
      form: {
        dogName: {
          value: '',
          valid: null,
          errorMsg: '',
        },
        breedId: {
          value: '',
          valid: null,
          errorMsg: '',
        },
        breedDesc: {
          value: '',
          valid: null,
          errorMsg: '',
        },
        country: {
          value: '',
          valid: null,
          errorMsg: '',
        },
        state: {
          value: '',
          valid: null,
          errorMsg: '',
        },
        province: {
          value: '',
          valid: null,
          errorMsg: '',
        },
        city: {
          value: '',
          valid: null,
          errorMsg: '',
        },
        fName: {
          value: '',
          valid: null,
          errorMsg: '',
        },
        lName: {
          value: '',
          valid: null,
          errorMsg: '',
        },
        captionLastName: {
          value: '',
        },
        specProjCode: {
          value: '',
          valid: null,
          errorMsg: '',
        },
        note: {
          value: '',
          valid: null,
          errorMsg: '',
        },
      },

      // Text of any overall form error, e.g. validation error, submission result error, etc.
      formErrorMsg: '',

      // Form validation: true: Every field has a valid value; false: At least one field has an invalid value
      formValid: false,

      // Form submission result: Result of form submission (when validated).
      // true: Result of submitted form action was an error; false: Initial state - no error (since form
      // not submitted yet); methods can "reset" this to false to simulate initial state any time after a
      // form submission.
      formError: false,

      // Flag: true: Form submit button was clicked; false: Reset to primitive state, i.e. form not submitted
      formSubmitted: false,

      componentKey: '',
    };
  },

  computed: {
    noteLength: function () {
      return this.form.note.value.length;
    },

    showForm: function () {
      return this.loadingSuccess && !this.showEditResult;
    },
    showFormActionError: function () {
      return (this.formSubmitted && !this.formValid) || this.formError;
    },
    resultHeader: function () {
      if (this.resultType === 'success') {
        return 'Success!';
      }
      if (this.resultType === 'error') {
        return 'There was a problem...';
      }
      return '';
    },
    resultMessage: function () {
      return this.resultErrorMsg ? this.resultErrorMsg : this.resultSuccessMsg;
    },
  },

  methods: {
    setSubmitted: function () {
      this.formSubmitted = true;
    },
    resetSubmitted: function () {
      this.formSubmitted = false;
    },
    setField: function (fieldValue, fieldName) {
      this.form[fieldName].value = fieldValue;
      // if (
      //   (fieldName === 'country' && fieldValue === 'US') ||
      //   fieldValue === ''
      // ) {
      //   this.form.province.value = '';
      //   this.setComponentKey(); // Trigger rerender of province field component so that value of '' shows in the input element
      // }
      this.fieldOnInput(fieldName);
    },

    fieldMaxLength: function (fieldName) {
      return Upload.getPropTruncateLength(fieldName) || 50;
    },

    // For given field fieldName, on input to the field, i.e. when the value changes, reset the
    // form state to its primitive/original state in terms of form error alerts, form field error alerts, etc.
    fieldOnInput: function (fieldName) {
      this.resetSubmitted(); // Reset 'form submitted' flag to false, i.e. "starting over" as tho form was never submitted.
      this.formError = false;
      if (this.form[fieldName].valid === false) {
        this.form[fieldName].valid = null; // Set to null to not show validation state feedback on the field, i.e. NO red/green styles
        // console.log('test method called on field', fieldName);
        // if (fieldName === 'country') {
        //   this.form.province.value = '';
        //   this.setComponentKey(); // Trigger rerender of province field component so that value of '' shows in the input element
        // }
      }
    },

    // Special case of 'setField' for the breedId field
    // Event value passed from SelectBreed child component is an object: { breedId: breedId, breed: name-of-breed}
    setBreed: function (breedData) {
      this.form.breedId.value = breedData.breedId;
      if (breedData.breedId !== 'mixed') {
        this.form.breedDesc.value = '';
      }
      this.fieldOnInput('breedId');
    },

    setComponentKey: function () {
      this.componentKey = Date.now();
    },

    validate: function () {
      var formData = {};
      Object.keys(this.form).forEach((fieldName) => {
        formData[fieldName] = this.form[fieldName].value;
      });

      // formData.imageFilename = this.imageFilename; // Need this to pass validation since imageFilename NOT in the this.form obj

      formData.specProjCode = formData.specProjCode.toLowerCase();
      var upload = Upload.update(formData); // Validation here so, test if validation passed next...

      console.log('upload data', upload);
      this.formValid = Upload.validationPassed();

      // Reset form field values to display latest 'state' of form after validation/sanitization/etc.
      Object.keys(upload).forEach((fieldName) => {
        this.form[fieldName].value = upload[fieldName];
      });

      if (!this.formValid) {
        this.formErrorMsg =
          'Ooops! Please correct the errors shown in the form above, then click Upload again.';
        Upload.validationErrors().forEach((err) => {
          var propName = Upload.getErrorProp(err);
          console.log('prop', propName);
          this.form[propName].valid = false; // Set to false to show validation state feedback of 'invalid', i.e. red styles
          this.form[propName].errorMsg = Upload.getErrorMsg(err);
          console.log(propName, Upload.getErrorMsg(err));
        });
      }
      this.upload = upload;
    },

    doEdit: async function () {
      var mainErrorMessagePrefix = 'Text edit of this image upload failed: ';
      var mainErrorMessage = `Looks like there was a problem with either the original
            upload or a "Change Image" upload (if you tried that at some point). You should DELETE this upload (on the Uploads list page), then try uploading again.`;

      // --------------- Validate form data
      this.validate();
      if (!this.formValid) return;

      var imageUploadRef = this.$fapi.getDocRefById(
        'imageUploads',
        this.documentId
      );

      // serverTimestamp
      // (per firestore rules, absolutely NEED this to allow update; update fails without this (helps deter rogue user
      // who is not aware of this))
      this.upload.updatedAt = this.$fapi.firebase.firestore.FieldValue.serverTimestamp();

      // Breed
      this.upload.breed = this.$store.state.breeds[
        this.form.breedId.value
      ].breedActual;
      this.upload.breedGeneral = this.$store.state.breeds[
        this.form.breedId.value
      ].breedGeneral;

      // Caption
      this.upload.caption = this.caption;

      console.log('edit text update obj:', Upload.getSnapshot(this.upload));

      try {
        // This update of the imageUpload doc will throw error due to fb security rules: Existing imageUpload doc
        // MUST have both image URLs populated; if either one is empty, it's a signal that
        // either image upload (orig or replacement) did not go to completion. If THAT'S the
        // case, user is notified to delete this upload.

        await imageUploadRef.update(Upload.getSnapshot(this.upload));

        // Populate the uploadedImage object for CardImage component success alert
        this.uploadedImage.dogName = this.upload.dogName;
        this.uploadedImage.breed = this.upload.breed;
        this.uploadedImage.caption = this.upload.caption;
        this.uploadedImage.imageThumbURL = this.imageThumbURL;

        this.resultType = 'success';
      } catch (err) {
        this.resultType = 'error';
        this.resultErrorMsg =
          mainErrorMessagePrefix + mainErrorMessage + ' Error Code: PE '; // err.message;
      }
      this.showEditResult = true;
    },
  },

  created: async function () {
    // console.log('ROUTE', this.$route);

    this.documentId = this.$route.params.id;

    try {
      var imageUploadRef = this.$fapi.getDocRefById(
        'imageUploads',
        this.$route.params.id
      );
      var uploadDoc = await imageUploadRef.get();
      if (!uploadDoc.exists) {
        throw Error('Document not found in the database.');
      }

      if (uploadDoc.data().usageStatus !== 'pending') {
        throw Error(
          'Permission denied: Selected, Published, or Unusable images cannot be edited.'
        );
      }

      // Initialize vue instance with data from db doc
      for (var field in this.form) {
        this.form[field].value = uploadDoc.data()[field] || '';
      }

      // Set imageFilename: Used in validation (since it's a required field but not being loaded in this form)
      // this.imageFilename = uploadDoc.data().imageFilename;

      this.caption = uploadDoc.data().caption;

      this.imageThumbURL = uploadDoc.data().imageThumbURL;

      this.loadingSuccess = true;
      // if (uploadDoc.exists) {
      //   // console.log(uploadDoc.data());
      //   for (var field in this.form) {
      //     this.form[field].value = uploadDoc.data()[field];
      //     // console.log(`${property}: ${uploadDoc.data()[property]}`);
      //   }
      // }
    } catch (err) {
      this.loadingError.isError = true;
      this.loadingError.message =
        'Failed to load Image Upload document for editing: ' + err.message;
      console.log('ERROR loading upload doc', err.message);
    }
  },
};
</script>

<style>
</style>